import React, { useState, useCallback } from 'react';
import { 
  Shuffle, 
  Save, 
  RefreshCw, 
  Download, 
  BarChart, 
  Save as SaveIcon, 
  PlusCircle,
  MinusCircle
} from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import SimpleOptionsSection from './SimpleOptionsSection';
import AdvancedOptionsSection from './AdvancedOptionsSection';
import DistributionSection from './DistributionSection';
import OutputFormatSection from './OutputFormatSection';

// Функция для генерации псевдослучайного числа с использованием seed
const seedRandom = (min, max, seed) => {
  if (!seed) return Math.random() * (max - min) + min;
  
  // Простой алгоритм генерации псевдослучайных чисел на основе seed
  const x = Math.sin(seed) * 10000;
  const rand = x - Math.floor(x);
  
  return min + rand * (max - min);
};

// Функция для генерации нормально распределенных случайных чисел (метод Бокса-Мюллера)
const randomNormal = (mean, stdDev, seed) => {
  let u = 0, v = 0;
  while (u === 0) u = seedRandom(0, 1, seed ? seed + 1 : null);
  while (v === 0) v = seedRandom(0, 1, seed ? seed + 2 : null);
  
  const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  return z * stdDev + mean;
};

// Алгоритм взвешенного случайного выбора
const weightedRandom = (weights) => {
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  let random = Math.random() * totalWeight;
  
  for (let i = 0; i < weights.length; i++) {
    random -= weights[i];
    if (random <= 0) {
      return i;
    }
  }
  return weights.length - 1;
};

/**
 * Компонент формы генератора случайных чисел
 */
const GeneratorForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isGenerating, 
  setIsGenerating,
  setResults,
  setHistory,
  history,
  setStatistics,
  showStatistics,
  setShowStatistics,
  saveConfig,
  results // Добавляем results в props
}) => {
  
  // Состояние для имени сохраняемой конфигурации
  const [saveConfigName, setSaveConfigName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };
      
      // Если переключаем на "уникальные числа", то максимальное число должно быть не меньше count
      if (name === 'unique' && checked && parseIntOrDefault(prev.count, 0) > (parseIntOrDefault(prev.max, 100) - parseIntOrDefault(prev.min, 1) + 1)) {
        newState.count = String(parseIntOrDefault(prev.max, 100) - parseIntOrDefault(prev.min, 1) + 1);
      }
      
      // Сбрасываем некоторые поля при изменении распределения
      if (name === 'distribution') {
        // Ничего не сбрасываем, только меняем распределение
      }
      
      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Обработчик изменения взвешенных значений
  const handleWeightedValueChange = (index, field, value) => {
    setFormData(prev => {
      const newWeightedValues = [...prev.weightedValues];
      newWeightedValues[index] = { 
        ...newWeightedValues[index], 
        [field]: value 
      };
      
      return {
        ...prev,
        weightedValues: newWeightedValues
      };
    });
  };

  // Добавление нового взвешенного значения
  const addWeightedValue = () => {
    setFormData(prev => {
      const lastValue = prev.weightedValues[prev.weightedValues.length - 1];
      const newValue = parseIntOrDefault(lastValue.value, 0) + 10;
      
      return {
        ...prev,
        weightedValues: [
          ...prev.weightedValues,
          { value: String(newValue), weight: '1' }
        ]
      };
    });
  };

  // Удаление взвешенного значения
  const removeWeightedValue = (index) => {
    setFormData(prev => {
      if (prev.weightedValues.length <= 2) return prev; // минимум 2 значения
      
      const newWeightedValues = [...prev.weightedValues];
      newWeightedValues.splice(index, 1);
      
      return {
        ...prev,
        weightedValues: newWeightedValues
      };
    });
  };

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      min, max, count, distribution, normalMean, normalDeviation,
      decimalPlaces, customRange, customRangeValues
    } = formData;

    // Парсинг числовых значений
    const numMin = parseFloatOrDefault(min, 1);
    const numMax = parseFloatOrDefault(max, 100);
    const numCount = parseIntOrDefault(count, 0);
    const numDecimalPlaces = parseIntOrDefault(decimalPlaces, 0);

    // Базовая валидация
    if (isNaN(numMin)) newErrors.min = 'Укажите корректное минимальное значение.';
    if (isNaN(numMax)) newErrors.max = 'Укажите корректное максимальное значение.';
    if (numMin >= numMax) newErrors.max = 'Максимальное значение должно быть больше минимального.';
    
    if (isNaN(numCount) || numCount <= 0) newErrors.count = 'Количество должно быть положительным числом.';
    if (numCount > 10000) newErrors.count = 'Максимальное количество - 10000 чисел.';
    
    // Проверка на уникальность и достаточный диапазон
    if (formData.unique && numCount > (numMax - numMin + 1)) {
      newErrors.count = `Для уникальных чисел количество не может превышать размер диапазона (${numMax - numMin + 1}).`;
    }

    // Дополнительная валидация для нормального распределения
    if (distribution === 'normal') {
      const numMean = parseFloatOrDefault(normalMean, 50);
      const numDeviation = parseFloatOrDefault(normalDeviation, 15);
      
      if (isNaN(numMean)) newErrors.normalMean = 'Укажите корректное среднее значение.';
      if (isNaN(numDeviation) || numDeviation <= 0) newErrors.normalDeviation = 'Стандартное отклонение должно быть положительным числом.';
    }

    // Валидация десятичных разрядов
    if (numDecimalPlaces < 0 || numDecimalPlaces > 10) {
      newErrors.decimalPlaces = 'Количество десятичных знаков должно быть от 0 до 10.';
    }

    // Валидация пользовательских диапазонов
    if (customRange) {
      if (!customRangeValues.trim()) {
        newErrors.customRangeValues = 'Укажите пользовательские диапазоны.';
      } else {
        const entries = customRangeValues.split(',');
        let totalCount = 0;
        
        for (const entry of entries) {
          const parts = entry.trim().split(':');
          if (parts.length !== 2) {
            newErrors.customRangeValues = 'Неверный формат. Используйте min-max:count, например 1-10:3';
            break;
          }
          
          const rangeParts = parts[0].split('-');
          if (rangeParts.length !== 2) {
            newErrors.customRangeValues = 'Неверный формат диапазона. Используйте min-max, например 1-10';
            break;
          }
          
          const rangeMin = parseIntOrDefault(rangeParts[0], NaN);
          const rangeMax = parseIntOrDefault(rangeParts[1], NaN);
          const rangeCount = parseIntOrDefault(parts[1], NaN);
          
          if (isNaN(rangeMin) || isNaN(rangeMax) || isNaN(rangeCount)) {
            newErrors.customRangeValues = 'Все значения должны быть числами.';
            break;
          }
          
          if (rangeMin >= rangeMax) {
            newErrors.customRangeValues = 'Максимальное значение должно быть больше минимального.';
            break;
          }
          
          if (rangeCount <= 0) {
            newErrors.customRangeValues = 'Количество должно быть положительным числом.';
            break;
          }
          
          totalCount += rangeCount;
        }
        
        if (totalCount !== numCount && !newErrors.customRangeValues) {
          newErrors.customRangeValues = `Сумма чисел из всех диапазонов (${totalCount}) должна равняться общему количеству (${numCount}).`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция генерации случайных чисел
  const generateRandomNumbers = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsGenerating(true);
    setResults([]);
    setStatistics(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const min = parseFloatOrDefault(formData.min, 1);
        const max = parseFloatOrDefault(formData.max, 100);
        const count = parseIntOrDefault(formData.count, 5);
        const unique = formData.unique;
        const sorting = formData.sorting;
        const distribution = formData.distribution;
        const normalMean = parseFloatOrDefault(formData.normalMean, 50);
        const normalDeviation = parseFloatOrDefault(formData.normalDeviation, 15);
        const decimalPlaces = parseIntOrDefault(formData.decimalPlaces, 0);
        const withoutRepetitions = formData.withoutRepetitions;
        const seed = formData.seed ? formData.seed.trim() : '';
        const seedNumber = seed ? hashString(seed) : null;
        const excludeValues = formData.excludeValues
          .split(',')
          .map(v => v.trim())
          .filter(v => v && !isNaN(parseFloatOrDefault(v, NaN)))
          .map(v => parseFloatOrDefault(v, 0));
        
        let generatedNumbers = [];
        
        // Для пользовательских диапазонов
        if (formData.customRange) {
          const ranges = formData.customRangeValues.split(',').map(entry => {
            const [rangeStr, countStr] = entry.trim().split(':');
            const [minStr, maxStr] = rangeStr.split('-');
            return {
              min: parseFloatOrDefault(minStr, 1),
              max: parseFloatOrDefault(maxStr, 100),
              count: parseIntOrDefault(countStr, 1)
            };
          });
          
          // Генерация чисел для каждого диапазона
          for (const range of ranges) {
            const rangeNumbers = generateNumbersFromRange(
              range.min, range.max, range.count, 
              distribution, unique, normalMean, normalDeviation, 
              seedNumber, excludeValues, decimalPlaces, withoutRepetitions
            );
            generatedNumbers = [...generatedNumbers, ...rangeNumbers];
          }
        } else {
          // Обычная генерация из одного диапазона
          generatedNumbers = generateNumbersFromRange(
            min, max, count, 
            distribution, unique, normalMean, normalDeviation, 
            seedNumber, excludeValues, decimalPlaces, withoutRepetitions
          );
        }
        
        // Сортировка результатов
        if (sorting === 'asc') {
          generatedNumbers.sort((a, b) => a - b);
        } else if (sorting === 'desc') {
          generatedNumbers.sort((a, b) => b - a);
        }
        
        // Округление до заданного числа десятичных знаков
        generatedNumbers = generatedNumbers.map(num => {
          return Number(num.toFixed(decimalPlaces));
        });
        
        // Расчет статистики
        const stats = calculateStatistics(generatedNumbers, min, max);
        
        // Сохранение результатов в историю
        const historyEntry = {
          id: Date.now().toString(),
          date: new Date().toISOString(),
          results: generatedNumbers,
          config: { ...formData },
          stats: stats
        };
        
        const updatedHistory = [historyEntry, ...history].slice(0, 10); // Храним только 10 последних генераций
        setHistory(updatedHistory);
        localStorage.setItem('generatorHistory', JSON.stringify(updatedHistory));
        
        setResults(generatedNumbers);
        setStatistics(stats);
        
      } catch (error) {
        console.error("Ошибка генерации:", error);
        setErrors({ general: `Произошла ошибка при генерации: ${error.message}. Пожалуйста, проверьте введенные данные.` });
        setResults([]);
        setStatistics(null);
      } finally {
        setIsGenerating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, history, setErrors, setHistory, setResults, setStatistics, setIsGenerating]);

  // Функция для генерации чисел из заданного диапазона
  const generateNumbersFromRange = (min, max, count, distribution, unique, normalMean, normalDeviation, seedNumber, excludeValues, decimalPlaces, withoutRepetitions) => {
    const result = [];
    const used = new Set();
    let lastValue = null;
    let attempts = 0;
    const maxAttempts = count * 10; // Предотвращение бесконечного цикла
    
    while (result.length < count && attempts < maxAttempts) {
      attempts++;
      let randomValue;
      
      // Генерация числа в зависимости от распределения
      if (distribution === 'normal') {
        // Для нормального распределения используем метод Бокса-Мюллера
        randomValue = randomNormal(normalMean, normalDeviation, seedNumber ? seedNumber + attempts : null);
        // Ограничиваем значение диапазоном min-max
        randomValue = Math.min(Math.max(randomValue, min), max);
      } else if (distribution === 'weighted') {
        // Для взвешенного распределения
        const weights = formData.weightedValues.map(item => parseFloatOrDefault(item.weight, 1));
        const values = formData.weightedValues.map(item => parseFloatOrDefault(item.value, 0));
        
        const idx = weightedRandom(weights);
        randomValue = values[idx];
      } else {
        // Для равномерного распределения
        randomValue = seedNumber
          ? min + (seedRandom(0, 1, seedNumber + attempts) * (max - min))
          : min + (Math.random() * (max - min));
      }
      
      // Округляем до целого, если нет десятичных знаков
      if (decimalPlaces <= 0) {
        randomValue = Math.floor(randomValue);
      }
      
      // Проверка на уникальность
      const valueKey = randomValue.toFixed(decimalPlaces);
      if (unique && used.has(valueKey)) {
        continue;
      }
      
      // Проверка на исключенные значения
      if (excludeValues.includes(randomValue)) {
        continue;
      }
      
      // Проверка на отсутствие близких повторений
      if (withoutRepetitions && lastValue === randomValue) {
        continue;
      }
      
      // Добавляем значение в результат
      result.push(randomValue);
      used.add(valueKey);
      lastValue = randomValue;
    }
    
    return result;
  };

  // Расчет статистики для сгенерированных чисел
  const calculateStatistics = (numbers, min, max) => {
    if (!numbers.length) return null;
    
    // Сортируем для расчета медианы
    const sorted = [...numbers].sort((a, b) => a - b);
    
    // Среднее арифметическое
    const mean = numbers.reduce((sum, val) => sum + val, 0) / numbers.length;
    
    // Медиана
    const median = numbers.length % 2 === 0
      ? (sorted[numbers.length / 2 - 1] + sorted[numbers.length / 2]) / 2
      : sorted[Math.floor(numbers.length / 2)];
    
    // Минимальное и максимальное значения
    const minValue = Math.min(...numbers);
    const maxValue = Math.max(...numbers);
    
    // Стандартное отклонение
    const variance = numbers.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / numbers.length;
    const stdDev = Math.sqrt(variance);
    
    // Мода (наиболее часто встречающееся значение)
    const frequency = {};
    let mode = null;
    let maxFreq = 0;
    
    for (const num of numbers) {
      frequency[num] = (frequency[num] || 0) + 1;
      if (frequency[num] > maxFreq) {
        maxFreq = frequency[num];
        mode = num;
      }
    }
    
    // Если все значения встречаются одинаково часто
    if (Object.values(frequency).every(freq => freq === 1)) {
      mode = "Нет";
    }
    
    // Распределение значений по интервалам для гистограммы
    const intervals = 10;
    const step = (max - min) / intervals;
    const distribution = Array(intervals).fill(0);
    
    for (const num of numbers) {
      const index = Math.min(Math.floor((num - min) / step), intervals - 1);
      distribution[index]++;
    }
    
    return {
      count: numbers.length,
      mean,
      median,
      mode,
      min: minValue,
      max: maxValue,
      range: maxValue - minValue,
      stdDev,
      distribution,
      distributionLabels: Array(intervals).fill(0).map((_, i) => ({
        min: min + i * step,
        max: min + (i + 1) * step
      }))
    };
  };

  // Функция для создания хеша из строки (для seed)
  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  };

  // Экспорт результатов в CSV или TXT
  const exportResults = (format = 'txt') => {
    if (!results || results.length === 0) {
      alert("Нет данных для экспорта. Сначала сгенерируйте случайные числа.");
      return;
    }

    // Получаем текущую статистику для экспорта
    const statistics = calculateStatistics(
      results, 
      parseFloatOrDefault(formData.min, 1), 
      parseFloatOrDefault(formData.max, 100)
    );

    let content = '';
    const filename = `случайные_числа_${new Date().toISOString().slice(0, 10)}`;
    
    if (format === 'csv') {
      // CSV формат
      content = 'Индекс,Значение\n';
      results.forEach((value, index) => {
        content += `${index + 1},${value}\n`;
      });
      
      // Добавляем статистику, если она доступна
      if (statistics) {
        content += '\nСтатистика\n';
        content += `Количество,${statistics.count}\n`;
        content += `Среднее,${statistics.mean.toFixed(4)}\n`;
        content += `Медиана,${statistics.median.toFixed(4)}\n`;
        content += `Минимум,${statistics.min}\n`;
        content += `Максимум,${statistics.max}\n`;
        content += `Разброс,${statistics.range.toFixed(4)}\n`;
        content += `Стд. отклонение,${statistics.stdDev.toFixed(4)}\n`;
      }
      
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.csv`;
      link.click();
      
    } else {
      // TXT формат
      let resultFormat = formData.format;
      switch (resultFormat) {
        case 'comma':
          content = results.join(', ');
          break;
        case 'newline':
          content = results.join('\n');
          break;
        case 'space':
          content = results.join(' ');
          break;
        default:
          content = results.join(', ');
      }
      
      // Добавляем статистику, если она доступна
      if (statistics) {
        content += '\n\nСтатистика:\n';
        content += `Количество: ${statistics.count}\n`;
        content += `Среднее: ${statistics.mean.toFixed(4)}\n`;
        content += `Медиана: ${statistics.median.toFixed(4)}\n`;
        content += `Минимум: ${statistics.min}\n`;
        content += `Максимум: ${statistics.max}\n`;
        content += `Разброс: ${statistics.range.toFixed(4)}\n`;
        content += `Стд. отклонение: ${statistics.stdDev.toFixed(4)}\n`;
      }
      
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.txt`;
      link.click();
    }
  };

  // Сброс формы
  const resetForm = () => {
    setFormData({
      min: '1',
      max: '100',
      count: '5',
      unique: true,
      sorting: 'none',
      distribution: 'uniform',
      weightedValues: [
        { value: '1', weight: '1' },
        { value: '50', weight: '5' },
        { value: '100', weight: '1' }
      ],
      normalMean: '50',
      normalDeviation: '15',
      excludeValues: '',
      format: 'comma',
      withoutRepetitions: false,
      seed: '',
      allowNegative: false,
      decimalPlaces: '0',
      customRange: false,
      customRangeValues: ''
    });
    setResults([]);
    setStatistics(null);
    setErrors({});
    setSaveConfigName('');
    setShowSaveDialog(false);
    setShowStatistics(false);
  };

  // Обработчик сохранения конфигурации
  const handleSaveConfig = () => {
    if (showSaveDialog) {
      const savedResult = saveConfig(saveConfigName || `Конфигурация от ${new Date().toLocaleDateString()}`);
      if (savedResult) {
        alert(`Конфигурация "${savedResult.name}" успешно сохранена!`);
      }
      setShowSaveDialog(false);
      setSaveConfigName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    generateRandomNumbers();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Shuffle className="w-6 h-6 text-indigo-500" />
          Параметры генерации
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры генератора */}
        <SimpleOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные настройки (расширенные опции) */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className="text-indigo-600 dark:text-indigo-400 text-sm font-medium flex items-center underline hover:text-indigo-800 dark:hover:text-indigo-300 focus:outline-none transition-colors"
          >
            {showAdvancedOptions ? 'Скрыть расширенные настройки' : 'Показать расширенные настройки'}
          </button>
        </div>

        {showAdvancedOptions && (
          <>
            {/* Расширенные опции */}
            <AdvancedOptionsSection 
              formData={formData} 
              handleInputChange={handleInputChange} 
              errors={errors} 
            />

            {/* Настройки распределения */}
            <DistributionSection 
              formData={formData} 
              handleInputChange={handleInputChange} 
              errors={errors}
              handleWeightedValueChange={handleWeightedValueChange}
              addWeightedValue={addWeightedValue}
              removeWeightedValue={removeWeightedValue}
            />
          </>
        )}

        {/* Формат вывода */}
        <OutputFormatSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения конфигурации */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveConfigName}
                onChange={(e) => setSaveConfigName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveConfig}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Shuffle className="w-5 h-5" />}
            disabled={isGenerating || Object.keys(errors).length > 0}
            isLoading={isGenerating}
            size="lg"
          >
            {isGenerating ? 'Генерация...' : 'Сгенерировать числа'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isGenerating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && results.length > 0 && (
            <Button
              type="button"
              onClick={() => exportResults('txt')}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isGenerating}
              size="lg"
            >
              Скачать TXT
            </Button>
          )}
          {results && results.length > 0 && (
            <Button
              type="button"
              onClick={() => exportResults('csv')}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isGenerating}
              size="lg"
            >
              Скачать CSV
            </Button>
          )}
          {results && results.length > 0 && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveConfig}
              icon={<SaveIcon className="w-5 h-5" />}
              variant="secondary"
              disabled={isGenerating}
              size="lg"
            >
              Сохранить настройки
            </Button>
          )}
          {results && results.length > 0 && (
            <Button
              type="button"
              onClick={() => setShowStatistics(!showStatistics)}
              icon={<BarChart className="w-5 h-5" />}
              variant="outline"
              disabled={isGenerating}
              size="lg"
            >
              {showStatistics ? 'Скрыть статистику' : 'Показать статистику'}
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default GeneratorForm;